import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { useChatContext } from "~/ai-chat/feature/use-chat-context";
import { useSystemBranching } from "~/branching/feature/use-system-branching";
import type { Branch, BranchId } from "~/branching/model/branch";
import { systemQueryOptions } from "~/systems/data-source/system-query-options";
import { useCanEditSystem } from "~/systems/feature/use-can-edit-system";

export type ChatSaveTarget =
  | { status: "none" }
  | { status: "loading" }
  | { status: "ready"; systemId: string; systemName: string; branch: Branch };

const NONE: ChatSaveTarget = { status: "none" };
const LOADING: ChatSaveTarget = { status: "loading" };

// Where a chat save can land besides the library: the system the user is looking at, on the
// branch in the URL or its default branch. Offered only when the user can edit that system.
export function useChatSaveTarget(): ChatSaveTarget {
  const { systemId, branchId, branchName } = useChatContext();
  const branching = useSystemBranching({
    systemId,
    // Route params are plain strings; the branch list is the authority on whether the id is real.
    desiredBranchId: (branchId as BranchId | undefined) ?? null,
    desiredBranchName: branchName ?? null,
  });
  const systemQuery = useQuery({ ...systemQueryOptions(systemId ?? ""), enabled: Boolean(systemId) });
  const access = useCanEditSystem(systemId);

  if (!systemId) return NONE;
  if (access.isLoading || branching.status === "loading" || systemQuery.isPending) return LOADING;
  if (!access.canEdit || !branching.selectedBranch || !systemQuery.data) return NONE;
  return { status: "ready", systemId, systemName: systemQuery.data.name, branch: branching.selectedBranch };
}

// A save card outlives the page it was answered on: the chat stays open while the user moves
// between systems. Keep the first settled target so the card goes on offering that system instead
// of quietly retargeting to whichever one is on screen when Save is clicked.
export function usePinnedChatSaveTarget(): ChatSaveTarget {
  const live = useChatSaveTarget();
  const [pinned, setPinned] = useState<ChatSaveTarget | null>(null);
  useEffect(() => {
    if (!pinned && live.status !== "loading") setPinned(live);
  }, [live, pinned]);
  return pinned ?? live;
}
